import { Controller } from "@hotwired/stimulus"
import flatpickr, { flatpickrLocale } from "../src/flatpickr"

const FLASH_HIDE_DELAY = 3000
const FLASH_FADE_DURATION = 150

const DATEPICKER_FORMATS = {
  date: "d.n.Y",
  datetime: "d.n.Y - H:i",
}

export default class extends Controller {
  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) { return }

    this.clickHandler = (event) => { this.handleClick(event) }
    this.changeHandler = (event) => { this.handleChange(event) }
    this.ajaxSuccessHandler = (event) => { this.handleAjaxSuccess(event) }

    this.element.addEventListener("click", this.clickHandler)
    this.element.addEventListener("change", this.changeHandler)
    this.element.addEventListener("ajax:success", this.ajaxSuccessHandler)

    this.initializeDatepickers()
    this.flashTimer = window.setTimeout(() => { this.hideFlashMessages() }, FLASH_HIDE_DELAY)
  }

  disconnect() {
    this.element.removeEventListener("click", this.clickHandler)
    this.element.removeEventListener("change", this.changeHandler)
    this.element.removeEventListener("ajax:success", this.ajaxSuccessHandler)

    window.clearTimeout(this.flashTimer)
    this.datepickers?.forEach((picker) => { picker.destroy() })
  }

  handleClick(event) {
    const flashMessage = event.target.closest(".flash.fade")
    if (flashMessage) { this.hideFlashMessage(flashMessage) }

    const couponOption = event.target.closest("[data-coupon-option]")
    if (couponOption) { this.selectCouponOption(couponOption) }

    const collapseTrigger = event.target.closest("[data-collapses]")
    if (collapseTrigger) { this.togglePanel(collapseTrigger) }

    const submitTrigger = event.target.closest("[data-trigger-submit]")
    if (submitTrigger) { this.submitTargetForm(submitTrigger) }

    const selectAllTrigger = event.target.closest("[data-country-select-select-all]")
    if (selectAllTrigger) {
      event.preventDefault()
      event.stopPropagation()
      this.updateCountrySelection(selectAllTrigger.dataset.countrySelectSelectAll, true)
    }

    const removeAllTrigger = event.target.closest("[data-country-select-remove-all]")
    if (removeAllTrigger) {
      event.preventDefault()
      event.stopPropagation()
      this.updateCountrySelection(removeAllTrigger.dataset.countrySelectRemoveAll, false)
    }

    const datepickerClearTrigger = event.target.closest("[data-datepicker-clear]")
    if (datepickerClearTrigger) { this.clearDatepicker(datepickerClearTrigger) }
  }

  handleChange(event) {
    const input = event.target

    if ("autosubmit" in input.dataset) { input.closest("form")?.requestSubmit() }
    if ("enables" in input.dataset) { this.toggleEnabledFields(input.dataset.enables) }
    if (input.matches("select#day-selector")) { input.closest("form")?.requestSubmit() }
  }

  handleAjaxSuccess(event) {
    const initiator = event.target.closest("[data-ajax-replacement]")
    if (!initiator) { return }

    const replacementSelector = initiator.dataset.replaceAfterAjax || "[data-ajax-replacement-parent]"
    const parent = initiator.closest(replacementSelector)
    const responseBody = event.detail?.[0]
    if (!parent || typeof responseBody !== "string") { return }

    const replacement = document.createRange().createContextualFragment(responseBody)
    parent.replaceWith(replacement)

    if (typeof window.initializeSorting === "function") { window.initializeSorting() }
  }

  selectCouponOption(option) {
    const parent = option.closest("[data-coupon-parent]")
    if (!parent) { return }

    parent.querySelectorAll("[data-coupon-option]").forEach((element) => {
      element.classList.remove("is-active")
    })

    option.classList.add("is-active")

    const input = parent.querySelector("[data-coupon-generation-method-input]")
    if (input) { input.value = option.dataset.couponGenerationMethod }
  }

  togglePanel(trigger) {
    const parent = trigger.closest("[data-collapsible-parent]")
    if (!parent) { return }

    const target = parent.querySelector(`[data-collapsible-target="${trigger.dataset.collapses}"]`)
    target?.classList.toggle("is--hidden")
    parent.classList.toggle("is--collapsed")
  }

  toggleEnabledFields(selectorList) {
    selectorList.split(",").forEach((selector) => {
      document.querySelectorAll(`[data-enable-target="${selector.trim()}"]`).forEach((target) => {
        target.classList.toggle("is--disabled")
      })
    })
  }

  submitTargetForm(trigger) {
    const form = document.querySelector(trigger.dataset.triggerSubmit)
    if (form) { form.requestSubmit() }
  }

  updateCountrySelection(selector, selected) {
    const select = document.querySelector(selector)
    if (!select) { return }

    select.querySelectorAll("option").forEach((option) => {
      if (!selected || !option.disabled) { option.selected = selected }
    })

    if (select.tomselect) {
      const values = Array.from(select.selectedOptions, (option) => option.value)
      select.tomselect.setValue(values)
    }
  }

  initializeDatepickers() {
    this.datepickers = Array.from(this.element.querySelectorAll("[data-datepicker]"), (element) => {
      const type = element.dataset.datepicker
      const configuration = {
        altInput: true,
        locale: flatpickrLocale(document.documentElement.lang),
        time_24hr: true,
      }

      if (element.dataset.datepickerMinimum) { configuration.minDate = element.dataset.datepickerMinimum }
      if (element.dataset.datepickerMaximum) { configuration.maxDate = element.dataset.datepickerMaximum }
      if ("datepickerAllowInput" in element.dataset) { configuration.allowInput = true }
      if (DATEPICKER_FORMATS[type]) { configuration.altFormat = DATEPICKER_FORMATS[type] }
      if (type === "datetime") { configuration.enableTime = true }

      return flatpickr(element, configuration)
    })
  }

  clearDatepicker(trigger) {
    const wrapper = trigger.closest(".datepicker__field-wrapper")
    const input = wrapper?.querySelector("[data-datepicker]")
    input?._flatpickr?.clear()
  }

  hideFlashMessages() {
    this.element.querySelectorAll(".flash.fade").forEach((element) => { this.hideFlashMessage(element) })
  }

  hideFlashMessage(element) {
    element.classList.remove("in")
    window.setTimeout(() => { element.hidden = true }, FLASH_FADE_DURATION)
  }
}
